import { type ReactNode, useRef, useState, type FC } from 'react'
import { Collapse as CollapseMui } from '@mui/material'
import classNames from 'classnames'
import { Icon } from 'shared/ui/Icon/Icon'
import { type TextStylesType, Typography } from 'shared/ui/Typography'
import { Tooltip } from 'shared/ui/Tooltip'
import styles from './styles.module.scss'

export type ICollapseProps = {
  isOpen?: boolean
  opened?: boolean
  children: ReactNode
  title: ReactNode
  titleVariant?: TextStylesType
  type?: 'withHover' | 'init'
  headerClassName?: string
  leftContent?: (isOpen: boolean) => ReactNode
  onClick?: (opened: boolean) => void
  className?: string
  variant?: 'default' | 'active' | 'transparent'
  error?: string
}

export const Collapse: FC<ICollapseProps> = ({
  children,
  isOpen = true,
  title,
  titleVariant = 'body-md-medium',
  headerClassName,
  leftContent,
  type,
  onClick,
  opened,
  className,
  variant = 'default',
  error,
}) => {
  const [localOpen, setLocalOpen] = useState(isOpen)
  const open = onClick ? !!opened : localOpen
  const isActiveStyle = open && variant === 'active'
  const ref = useRef<HTMLDivElement | null>(null)
  const [isErrorVisible, setErrorVisibility] = useState(false)

  const handleClick = () => {
    if (onClick) {
      onClick(!open)
    } else {
      setLocalOpen(!open)
    }
  }

  return (
    <div className={classNames(styles.container, className)}>
      <div
        ref={ref}
        className={classNames(styles.header, headerClassName, {
          [styles.withHover]: type === 'withHover',
          [styles.isOpen]: open,
          [styles.isActive]: isActiveStyle,
          [styles.transparent]: variant === 'transparent',
        })}
        onClick={handleClick}
        onMouseEnter={() => setErrorVisibility(true)}
        onMouseLeave={() => setErrorVisibility(false)}
      >
        {leftContent?.(open)}
        <Typography variant={titleVariant} ariaLabel={'Collapse_Title'}>
          {title}
        </Typography>
        {!!error && (
          <Tooltip
            color='red'
            placement='bottom'
            open={isErrorVisible}
            label={error}
            PopperProps={{
              anchorEl: () => ref.current as HTMLDivElement,
            }}
            breakWord
          >
            <Icon icon='alertTriangle' fontSize={16} color={'var(--content-negative-primary)'} />
          </Tooltip>
        )}
        <Icon
          icon={open ? 'chevronUp' : 'chevronDown'}
          color='var(--gray-60)'
          className={classNames(styles.toggleIcon, {
            [styles.isActive]: isActiveStyle,
          })}
        />
      </div>

      <CollapseMui in={open}>
        <div>{children}</div>
      </CollapseMui>
    </div>
  )
}
